/**
 * ShareDesignModal.jsx — publish / copy-link / unpublish for a single
 * design generation. Called from the Design Studio "Share" button.
 *
 * Idempotent — hitting Publish twice returns the SAME token so previously
 * shared URLs keep working.
 */
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Copy, Check, Link as LinkIcon, Loader2, ExternalLink } from 'lucide-react';
import { designApi } from '../../services/drawings';
import { toast } from '../../stores';

export const ShareDesignModal = ({ open, onClose, gen, onPublished }) => {
  const [publishing, setPublishing] = useState(false);
  const [copied, setCopied] = useState(false);
  const token = gen?.public_token;
  const url = token ? designApi.publicUrl(token) : null;

  const doPublish = async () => {
    if (!gen?.id) return;
    setPublishing(true);
    try {
      const { data } = await designApi.publish(gen.id);
      onPublished?.({ ...gen, public_token: data.token });
      toast.success('Design published — share the link');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Publish failed');
    } finally {
      setPublishing(false);
    }
  };

  const doUnpublish = async () => {
    if (!gen?.id) return;
    setPublishing(true);
    try {
      await designApi.unpublish(gen.id);
      onPublished?.({ ...gen, public_token: null });
      toast.success('Design unpublished — the link now 404s');
    } catch (e) {
      toast.error('Unpublish failed');
    } finally {
      setPublishing(false);
    }
  };

  const copyUrl = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch (_e) {
      toast.error('Copy failed — select and copy the URL manually');
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
          onClick={onClose}
          data-testid="ds-share-modal">
          <motion.div
            initial={{ scale: 0.94, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.94, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-xl p-5 shadow-2xl"
            style={{ background: 'var(--layer-0)', border: '1px solid var(--hairline)' }}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="text-[16px] font-bold text-nexus-text-primary flex items-center gap-1.5">
                  <LinkIcon size={14} style={{ color: '#F26B1F' }} /> Share this design
                </h3>
                <p className="text-[11.5px] text-nexus-text-tertiary mt-0.5">
                  Anyone with the link can view — read-only, no login required.
                </p>
              </div>
              <button onClick={onClose} className="p-1 rounded hover:bg-nexus-bg-tertiary text-nexus-text-tertiary" data-testid="ds-share-close">
                <X size={14} />
              </button>
            </div>

            {token ? (
              <>
                <div className="rounded-lg p-2 flex items-center gap-2 mb-2"
                  style={{ background: 'var(--layer-1)', border: '1px solid var(--hairline)' }}>
                  <input
                    readOnly value={url}
                    className="flex-1 bg-transparent outline-none text-[11.5px] text-nexus-text-primary font-mono truncate"
                    data-testid="ds-share-url"
                  />
                  <button onClick={copyUrl}
                    className="px-2 py-1 rounded text-[11px] font-semibold flex items-center gap-1"
                    style={{ background: copied ? '#10b981' : '#F26B1F', color: '#fff' }}
                    data-testid="ds-share-copy">
                    {copied ? <><Check size={11} /> Copied</> : <><Copy size={11} /> Copy</>}
                  </button>
                </div>
                <div className="flex items-center justify-between text-[11px]">
                  <a href={url} target="_blank" rel="noreferrer"
                    className="flex items-center gap-1 hover:underline" style={{ color: '#F26B1F' }}
                    data-testid="ds-share-open">
                    Open in a new tab <ExternalLink size={10} />
                  </a>
                  <button onClick={doUnpublish} disabled={publishing}
                    className="text-nexus-text-tertiary hover:text-red-500 flex items-center gap-1"
                    data-testid="ds-share-unpublish">
                    {publishing ? <Loader2 size={10} className="animate-spin" /> : null}
                    Unpublish
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="text-[12px] text-nexus-text-primary mb-3">
                  Publishing creates a stable URL that always shows the CURRENT version of this drawing.
                  Refining after publishing updates the shared page instantly.
                </p>
                <button onClick={doPublish} disabled={publishing}
                  className="w-full py-2.5 rounded-md text-[13px] font-semibold flex items-center justify-center gap-1.5"
                  style={{ background: '#F26B1F', color: '#fff' }}
                  data-testid="ds-share-publish">
                  {publishing ? <Loader2 size={13} className="animate-spin" /> : <LinkIcon size={13} />}
                  {publishing ? 'Publishing…' : 'Publish & get share link'}
                </button>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ShareDesignModal;
